import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient, useLanguage } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import PageHeader from "../components/layout/PageHeader";
import ArabicText from "../components/shared/ArabicText";
import IslamicLoadingScreen from "../components/shared/IslamicLoadingScreen";
import { SURAHS, getSurahTranslations, getSurahFirstAyahMeta, getRandomAyah, getAyahByKey } from "../services/quranService";
import { quranApi } from "../services/quranApi";
import { quranCache } from "../services/quranCache";
import { Clock, CheckCircle2, XCircle, Trophy, ChevronRight, RotateCcw, Home } from "lucide-react";

const QUESTION_TYPES = [
  "number_to_name",
  "meaning_to_name",
  "arabic_to_number",
  "name_to_meaning",
  "revelation_type",
  "number_of_ayahs",
  "total_surahs",
  "name_to_number",
  "arabic_to_name",
  "juz_first_ayah",
  "hizb_first_ayah",
];
const NAME_ONLY_TYPES = [
  "number_to_name",
  "arabic_to_name",
  "arabic_to_number",
  "name_to_number",
];
const TRANSLATION_TYPES = [
  "meaning_to_name",
  "name_to_meaning",
];
const AYAH_COMPLETION_TYPES = [
  "first_word_after",
  "last_word",
  "bridge_next_ayah",
  "ayah_number",
  "surah_name",
  "page_number",
  "juz_number",
  "hizb_number",
  "word_to_meaning",
  "meaning_to_word",
  "continue_three",
  "continue_start",
  "continue_middle",
  "continue_end",
  "audio_continue",
  "ayah_snippet_to_translation",
  "translation_to_ayah_snippet",
];

const TRANSLATION_QUIZ_TYPES = [
  "ayah_snippet_to_translation",
  "translation_to_ayah_snippet",
];

const AYAH_MODE_TYPES = {
  easy: ["continue_start", "first_word_after", "ayah_number", "surah_name", "word_to_meaning", "ayah_snippet_to_translation"],
  medium: ["continue_middle", "continue_three", "last_word", "bridge_next_ayah", "meaning_to_word", "translation_to_ayah_snippet"],
  hard: ["continue_end", "last_word", "page_number", "juz_number", "hizb_number", "meaning_to_word", "word_to_meaning", "ayah_snippet_to_translation", "translation_to_ayah_snippet"],
  hafiz: ["audio_continue"],
};
const TOTAL_QUESTIONS = 10;

// Generate fallback questions if API fails
function generateFallbackQuestions(count, isEnglish) {
  const result = [];
  const usedSurahs = new Set();

  for (let i = 0; i < count && i < SURAHS.length; i++) {
    // Pick a random surah
    let randomSurah;
    let attempts = 0;
    do {
      randomSurah = SURAHS[Math.floor(Math.random() * SURAHS.length)];
      attempts++;
    } while (usedSurahs.has(randomSurah.number) && attempts < 10);

    if (attempts >= 10) break;
    usedSurahs.add(randomSurah.number);

    // Generate different question types
    const questionTypes = [
      {
        q: isEnglish
          ? `How many verses are in Surah ${randomSurah.englishName}?`
          : `Berapa jumlah ayat dalam Surah ${randomSurah.englishName}?`,
        opts: buildNumberOptions(randomSurah.numberOfAyahs, 3, 286),
        ans: String(randomSurah.numberOfAyahs),
      },
      {
        q: isEnglish
          ? `What is the number of Surah ${randomSurah.englishName}?`
          : `Surah ${randomSurah.englishName} nomor ke berapa?`,
        opts: buildNumberOptions(randomSurah.number, 1, 114),
        ans: String(randomSurah.number),
      },
      {
        q: isEnglish
          ? `Is Surah ${randomSurah.englishName} Meccan or Medinan?`
          : `Surah ${randomSurah.englishName} termasuk Makkiyah atau Madaniyah?`,
        opts: [getRevelationLabel("Meccan", isEnglish), getRevelationLabel("Medinan", isEnglish)].sort(() => Math.random() - 0.5),
        ans: getRevelationLabel(randomSurah.revelationType, isEnglish),
      },
    ];

    const questionType = questionTypes[i % questionTypes.length];
    result.push({
      id: i,
      ...questionType,
    });
  }

  return result;
}

function pickWrongSurahs(correctIndex, count, poolOverride) {
  const poolBase = poolOverride?.length ? poolOverride : SURAHS;
  const pool = poolBase.filter((_, idx) => idx !== correctIndex);
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

function getMeaningLabel(surah, translations) {
  return translations[surah.number] || surah.englishNameTranslation;
}

function buildNumberOptions(correctValue, min, max) {
  const options = new Set([String(correctValue)]);
  while (options.size < 4) {
    const val = Math.floor(Math.random() * (max - min + 1)) + min;
    options.add(String(val));
  }
  return Array.from(options).sort(() => Math.random() - 0.5);
}

function getRevelationLabel(type, isEnglish) {
  if (isEnglish) return type === "Meccan" ? "Meccan" : "Medinan";
  return type === "Meccan" ? "Makkiyah" : "Madaniyah";
}

function containsArabic(text) {
  return /[\u0600-\u06FF]/.test(text || "");
}

function getVerseWords(verse) {
  const rawText = verse?.text_uthmani || verse?.text_uthmani_simple || verse?.text || "";
  if (verse?.words?.length) {
    const wordTexts = verse.words.map((word) => word.text_uthmani || word.text || "").filter(Boolean);
    if (wordTexts.length > 0 && wordTexts.some(containsArabic)) {
      return wordTexts;
    }
  }
  if (rawText) return rawText.split(/\s+/).filter(Boolean);
  return [];
}

function getVerseText(verse) {
  if (verse?.text_uthmani || verse?.text_uthmani_simple || verse?.text) {
    return verse.text_uthmani || verse.text_uthmani_simple || verse.text;
  }
  return getVerseWords(verse).join(" ");
}

function getWordPairs(verse) {
  if (!verse?.words?.length) return [];
  return verse.words
    .map((word) => ({
      arabic: word.text_uthmani || word.text || "",
      meaning: typeof word.text_indop === "string" ? word.text_indop : word.text_indop?.text || (typeof word.translation === "string" ? word.translation : word.translation?.text || ""),
    }))
    .filter((pair) => pair.arabic && pair.meaning);
}

function pickWrongWords(words, correct, count) {
  const pool = words.filter((w) => w !== correct);
  const shuffled = [...pool].sort(() => Math.random() - 0.5);
  const opts = [correct, ...shuffled.slice(0, count)];
  return opts.sort(() => Math.random() - 0.5);
}

function buildContinuationOptions(allWords, correctPhrase, count) {
  const options = new Set([correctPhrase]);
  const maxStart = Math.max(0, allWords.length - 3);
  while (options.size < count && maxStart > 0) {
    const start = Math.floor(Math.random() * maxStart);
    const phrase = allWords.slice(start, start + 3).join(" ");
    if (phrase) options.add(phrase);
  }
  return Array.from(options).sort(() => Math.random() - 0.5);
}

async function getVerseTranslation(verse, isEnglish) {
  // Check if verse already has translation
  if (isEnglish && verse.english) {
    return verse.english;
  }
  if (!isEnglish && verse.indonesian) {
    return verse.indonesian;
  }

  // Try to get translation from verse object
  if (isEnglish && verse.translation?.text) {
    return verse.translation.text;
  }
  if (!isEnglish && verse.translation?.text_id) {
    return verse.translation.text_id;
  }

  // Fetch from API as last resort
  try {
    const dueAyahs = getDueMemorizedAyahs(surahNumber, memorizedAyahsList);
    if (dueAyahs.length > 0) {
      const priorityAyahTypes = ["continue_start", "continue_middle", "continue_end", "ayah_number", "surah_name"];
      const maxPriority = Math.min(6, count);
      let prAttempts = 0;
      while (result.length < maxPriority && prAttempts < maxPriority * 3) {
        prAttempts += 1;
        try {
          const type = priorityAyahTypes[Math.floor(Math.random() * priorityAyahTypes.length)];
          const question = await withTimeout(buildAyahQuestion(type, isEnglish, "medium", [surahNumber]), 3500);
          if (question) {
            const signature = getQuestionSignature(question);
            if (signature && !seen.has(signature)) {
              seen.add(signature);
              result.push({ id: result.length, ...question });
            }
          }
        } catch (error) {
          continue;
        }
      }
    }
    const verseKey = verse.verse_key || verse.verse_key || `${verse.surah_number || verse.surah}:${verse.verse_number || verse.number}`;
    const [surahNum, ayahNum] = verseKey.split(':').map(Number);

    if (!surahNum || !ayahNum) return '';

    const lang = isEnglish ? 'en.sahih' : 'id.indonesian';
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000); // 2 second timeout

    const response = await fetch(`https://api.alquran.cloud/v1/ayah/${surahNum}:${ayahNum}/${lang}`, {
      signal: controller.signal
    });
    clearTimeout(timeoutId);

    if (!response.ok) return '';

    const data = await response.json();

    if (data?.code === 200 && data?.data?.text) {
      return data.data.text;
    }
  } catch (error) {
    // Silently fail
  }

  return '';
}

function generateVerseSnippet(allWords, wordCount = 3) {
  if (allWords.length < wordCount) return allWords.join(' ');

  const maxStart = allWords.length - wordCount;
  const start = Math.floor(Math.random() * (maxStart + 1));
  return allWords.slice(start, start + wordCount).join(' ');
}

async function buildTranslationOptions(correctTranslation, count, isEnglish) {
  const options = new Set([correctTranslation]);

  // Simple wrong options based on modifying correct translation
  const modifiers = isEnglish
    ? ["Allah", "Lord", "the believers", "the disbelievers", "the heavens", "the earth"]
    : ["Allah", "Tuhan", "orang beriman", "orang kafir", "langit", "bumi"];

  for (const mod of modifiers) {
    if (options.size >= count) break;
    // Create simple wrong translations by replacing words
    if (correctTranslation.length > 20 && !correctTranslation.includes(mod)) {
      const wrongTranslation = correctTranslation.split(' ').slice(0, 5).join(' ') + ' ' + mod + '...';
      if (wrongTranslation !== correctTranslation) {
        options.add(wrongTranslation);
      }
    }
  }

  // If we still need more options, use API (limited)
  if (options.size < count) {
    const wrongSurahs = pickWrongSurahs(Math.floor(Math.random() * 114), Math.min(3, count - options.size));
    const lang = isEnglish ? 'en.sahih' : 'id.indonesian';

    for (const surah of wrongSurahs) {
      if (options.size >= count) break;

      try {
        const randomAyah = Math.floor(Math.random() * surah.numberOfAyahs) + 1;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 1500); // 1.5 second timeout

        const response = await fetch(`https://api.alquran.cloud/v1/ayah/${surah.number}:${randomAyah}/${lang}`, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);

        if (response.ok) {
          const data = await response.json();
          if (data?.code === 200 && data?.data?.text) {
            const translation = data.data.text;
            if (translation !== correctTranslation && translation.length > 10) {
              options.add(translation);
            }
          }
        }
      } catch (error) {
        // Skip failed fetches
      }
    }
  }

  return Array.from(options).sort(() => Math.random() - 0.5);
}

function buildSnippetOptions(allWords, correctSnippet, count) {
  const options = new Set([correctSnippet]);
  const wordCount = correctSnippet.split(' ').length;

  // Generate wrong snippets from different parts of the verse
  let attempts = 0;
  while (options.size < count && attempts < 20) {
    attempts++;
    const snippet = generateVerseSnippet(allWords, wordCount);
    if (snippet && snippet !== correctSnippet) {
      options.add(snippet);
    }
  }

  return Array.from(options).sort(() => Math.random() - 0.5);
}

function getQuestionSignature(question) {
  if (!question) return "";
  const stem = question.stem || "";
  const meta = question.meta || "";
  const audio = question.audio || "";
  return `${question.q}|${stem}|${meta}|${question.ans}|${audio}`;
}

function withTimeout(promise, ms) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error("timeout")), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

const SRS_STORE_KEY = "quran-srs-store";
function readSrsStore() {
  try {
    const raw = localStorage.getItem(SRS_STORE_KEY) || "{}";
    const obj = JSON.parse(raw);
    if (obj && typeof obj === "object") return obj;
    return {};
  } catch {
    return {};
  }
}
function writeSrsStore(obj) {
  try {
    localStorage.setItem(SRS_STORE_KEY, JSON.stringify(obj));
  } catch {}
}
function parseVerseKeyFromMeta(meta) {
  if (!meta || typeof meta !== "string") return null;
  const parts = meta.trim().split(/\s+/);
  const last = parts[parts.length - 1];
  if (!last || !last.includes(":")) return null;
  const [s, a] = last.split(":");
  const sn = Number(s);
  const an = Number(a);
  if (!sn || !an) return null;
  return `${sn}:${an}`;
}
function updateSrs(verseKey, correct) {
  if (!verseKey) return;
  const store = readSrsStore();
  const now = Date.now();
  const entry = store[verseKey] || { easiness: 2.5, interval: 1, repetitions: 0, lastReviewed: 0, dueAt: 0 };
  const q = correct ? 5 : 2;
  const e = Math.max(1.3, entry.easiness + (0.1 - (5 - q) * (0.08 + (5 - q) * 0.02)));
  let reps = entry.repetitions;
  let interval = entry.interval;
  if (q < 3) {
    reps = 0;
    interval = 1;
  } else {
    reps += 1;
    if (reps === 1) interval = 1;
    else if (reps === 2) interval = 6;
    else interval = Math.ceil(interval * e);
  }
  const dueAt = now + interval * 24 * 60 * 60 * 1000;
  store[verseKey] = { easiness: e, interval, repetitions: reps, lastReviewed: now, dueAt };
  writeSrsStore(store);
}
function isDueSrs(srsEntry) {
  if (!srsEntry) return true;
  return Date.now() >= (srsEntry.dueAt || 0);
}
function getDueMemorizedAyahs(surahNumber, memorizedAyahsList) {
  const store = readSrsStore();
  const keys = (memorizedAyahsList || []).map((a) => `${surahNumber}:${a}`);
  const due = keys.filter((k) => isDueSrs(store[k]));
  return due.map((k) => Number(k.split(":")[1]));
}

function getAyahQuestionTypes(mode) {
  return AYAH_MODE_TYPES[mode] || AYAH_COMPLETION_TYPES;
}

async function buildQuestion(type, surah, surahIndex, isEnglish, translations, getMeta, wrongPool) {
  if (type === "number_to_name") {
    const wrong = pickWrongSurahs(surahIndex, 3, wrongPool).map((x) => x.englishName);
    const opts = [surah.englishName, ...wrong].sort(() => Math.random() - 0.5);
    return {
      q: isEnglish ? `What is the name of surah number ${surah.number}?` : `Apa nama surah ke-${surah.number}?`,
      opts,
      ans: surah.englishName,
    };
  }
  if (type === "meaning_to_name") {
    const meaning = getMeaningLabel(surah, translations);
    if (!meaning) return null;
    const wrong = pickWrongSurahs(surahIndex, 3, wrongPool).map((x) => x.englishName);
    const opts = [surah.englishName, ...wrong].sort(() => Math.random() - 0.5);
    return {
      q: isEnglish ? `Which surah matches this meaning: "${meaning}"?` : `Arti berikut milik surah apa: "${meaning}"?`,
      opts,
      ans: surah.englishName,
    };
  }
  if (type === "arabic_to_number") {
    const wrong = pickWrongSurahs(surahIndex, 3, wrongPool).map((x) => String(x.number));
    const opts = [String(surah.number), ...wrong].sort(() => Math.random() - 0.5);
    return {
      q: isEnglish ? `What is the surah number for "${surah.name}"?` : `Nama Arab "${surah.name}" itu surah nomor berapa?`,
      opts,
      ans: String(surah.number),
    };
  }
  if (type === "arabic_to_name") {
    const wrong = pickWrongSurahs(surahIndex, 3, wrongPool).map((x) => x.englishName);
    const opts = [surah.englishName, ...wrong].sort(() => Math.random() - 0.5);
    return {
      q: isEnglish ? `What is the name of this surah: "${surah.name}"?` : `Apa nama surah ini: "${surah.name}"?`,
      opts,
      ans: surah.englishName,
    };
  }
  if (type === "name_to_meaning") {
    const correctMeaning = getMeaningLabel(surah, translations);
    if (!correctMeaning) return null;
    const wrongMeanings = pickWrongSurahs(surahIndex, 3, wrongPool).map((x) => getMeaningLabel(x, translations));
    const opts = [correctMeaning, ...wrongMeanings].sort(() => Math.random() - 0.5);
    return {
      q: isEnglish ? `What is the meaning of Surah ${surah.englishName}?` : `Apa arti dari Surah ${surah.englishName}?`,
      opts,
      ans: correctMeaning,
    };
  }
  if (type === "revelation_type") {
    const opts = [getRevelationLabel("Meccan", isEnglish), getRevelationLabel("Medinan", isEnglish)];
    return {
      q: isEnglish ? `Is Surah ${surah.englishName} Meccan or Medinan?` : `Surah ${surah.englishName} termasuk Makkiyah atau Madaniyah?`,
      opts: opts.sort(() => Math.random() - 0.5),
      ans: getRevelationLabel(surah.revelationType, isEnglish),
    };
  }
  if (type === "number_of_ayahs") {
    const opts = buildNumberOptions(surah.numberOfAyahs, 3, 286);
    return {
      q: isEnglish ? `How many verses are in Surah ${surah.englishName}?` : `Berapa jumlah ayat Surah ${surah.englishName}?`,
      opts,
      ans: String(surah.numberOfAyahs),
    };
  }
  if (type === "name_to_number") {
    const opts = buildNumberOptions(surah.number, 1, 114);
    return {
      q: isEnglish ? `Surah ${surah.englishName} is number...?` : `Surah ${surah.englishName} nomor ke berapa?`,
      opts,
      ans: String(surah.number),
    };
  }
  if (type === "total_surahs") {
    const opts = buildNumberOptions(114, 100, 120);
    return {
      q: isEnglish ? "How many surahs are in the Qur'an?" : "Berapa jumlah surah dalam Al-Qur'an?",
      opts,
      ans: "114",
    };
  }
  if (type === "juz_first_ayah") {
    const meta = await getMeta(surah.number);
    if (!meta?.juz) return null;
    const opts = buildNumberOptions(meta.juz, 1, 30);
    return {
      q: isEnglish ? `Which juz contains the first ayah of Surah ${surah.englishName}?` : `Ayat pertama Surah ${surah.englishName} termasuk juz berapa?`,
      opts,
      ans: String(meta.juz),
    };
  }
  if (type === "hizb_first_ayah") {
    const meta = await getMeta(surah.number);
    if (!meta?.hizbQuarter) return null;
    const hizbNumber = Math.ceil(meta.hizbQuarter / 4);
    const opts = buildNumberOptions(hizbNumber, 1, 60);
    return {
      q: isEnglish ? `Which hizb contains the first ayah of Surah ${surah.englishName}?` : `Ayat pertama Surah ${surah.englishName} termasuk hizb ke berapa?`,
      opts,
      ans: String(hizbNumber),
    };
  }
  return null;
}

async function generateQuiz(count, isEnglish, translations, getMeta, questionTypes) {
  const result = [];
  const used = [];
  const seen = new Set();
  let attempts = 0;

  // Separate question types into surah-based and ayah-based
  const ayahBasedTypes = ["ayah_snippet_to_translation", "translation_to_ayah_snippet"];
  const surahBasedTypes = questionTypes.filter(t => !ayahBasedTypes.includes(t));
  const hasAyahTypes = questionTypes.some(t => ayahBasedTypes.includes(t));

  while (result.length < count && attempts < count * 40) {
    attempts += 1;

    let question = null;

    // Try ayah-based types first (for translation quiz)
    if (hasAyahTypes && result.length < Math.ceil(count / 2)) {
      const typePool = questionTypes.filter(t => ayahBasedTypes.includes(t));
      if (typePool.length > 0) {
        const type = typePool[Math.floor(Math.random() * typePool.length)];
        try {
          question = await buildAyahQuestion(type, isEnglish, "easy", null);
        } catch (error) {
          // Skip on error
          continue;
        }
      }
    }

    // Then try surah-based types
    if (!question && surahBasedTypes.length > 0) {
      let idx = Math.floor(Math.random() * SURAHS.length);
      while (used.includes(idx)) idx = Math.floor(Math.random() * SURAHS.length);
      if (idx >= SURAHS.length) continue;
      used.push(idx);
      const s = SURAHS[idx];
      const typePool = [...surahBasedTypes];
      while (typePool.length > 0 && !question) {
        const type = typePool.splice(Math.floor(Math.random() * typePool.length), 1)[0];
        question = await buildQuestion(type, s, idx, isEnglish, translations, getMeta);
      }
    }

    if (question) {
      const signature = getQuestionSignature(question);
      if (signature && !seen.has(signature)) {
        seen.add(signature);
        result.push({ id: result.length, ...question });
      }
    }
  }

  // If we still don't have enough questions, add fallback surah questions
  if (result.length < count) {
    const fallbackQuestions = generateFallbackQuestions(count - result.length, isEnglish);
    for (const fq of fallbackQuestions) {
      const signature = getQuestionSignature(fq);
      if (!seen.has(signature)) {
        seen.add(signature);
        result.push({ id: result.length, ...fq });
      }
    }
  }

  return result.slice(0, count);
}

async function getRandomVerse(surahNumbers) {
  const pool = surahNumbers?.length ? SURAHS.filter((s) => surahNumbers.includes(s.number)) : SURAHS;
  if (pool.length === 0) return null;
  const surah = pool[Math.floor(Math.random() * pool.length)];
  const verseNumber = Math.floor(Math.random() * surah.numberOfAyahs) + 1;

  // Check cache first
  const cached = quranCache.getVerse(surah.number, verseNumber);
  if (cached) {
    return { verse: cached, surah };
  }

  try {
    const verse = await withTimeout(quranApi.getVerse(surah.number, verseNumber), 5000); // Reduce timeout
    const verseText = getVerseText(verse);
    if (!containsArabic(verseText)) {
      try {
        const fallback = await withTimeout(getAyahByKey(surah.number, verseNumber), 5000);
        verse.text_uthmani = fallback.arabic;
        verse.juz = fallback.juz;
        verse.page = fallback.page;
        verse.hizb_quarter = fallback.hizbQuarter;
      } catch (fallbackError) {}
    }

    // Cache the verse
    quranCache.setVerse(surah.number, verseNumber, verse);
    return { verse, surah };
  } catch (error) {
    try {
      const fallback = await withTimeout(getAyahByKey(surah.number, verseNumber), 5000);
      const verse = {
        verse_number: fallback.number,
        verse_key: `${fallback.surah.number}:${fallback.number}`,
        text_uthmani: fallback.arabic,
        juz: fallback.juz,
        page: fallback.page,
        hizb_quarter: fallback.hizbQuarter,
      };
      quranCache.setVerse(surah.number, verseNumber, verse);
      return { verse, surah };
    } catch (fallbackError) {
      try {
        const fallback = await withTimeout(getRandomAyah(), 5000);
        const fallbackSurah = SURAHS.find((item) => item.number === fallback.surah.number) || surah;
        const verse = {
          verse_number: fallback.number,
          verse_key: `${fallback.surah.number}:${fallback.number}`,
          text_uthmani: fallback.arabic,
          juz: fallback.juz,
          page: fallback.page,
          hizb_quarter: fallback.hizbQuarter,
        };
        quranCache.setVerse(fallbackSurah.number, fallback.number, verse);
        return { verse, surah: fallbackSurah };
      } catch (lastError) {
        return null;
      }
    }
  }
}

async function buildAyahQuestion(type, isEnglish, mode, surahNumbers) {
  const data = await getRandomVerse(surahNumbers);
  if (!data) return null;
  const { verse, surah } = data;
  const surahIndex = SURAHS.findIndex((item) => item.number === surah.number);
  const words = getVerseWords(verse);
  if (words.length < 4) return null;
  const verseText = getVerseText(verse);
  const hideText = mode === "hafiz";
  const surahName = surah.englishName;
  const ayahNumberRaw = verse.verse_number || verse.verseNumber || verse.verse_key?.split(":")[1];
  const ayahNumber = ayahNumberRaw ? Number(ayahNumberRaw) : null;
  const pageNumber = verse.page_number || verse.page || verse.pageNumber;
  const juzNumber = verse.juz_number || verse.juz;
  const hizbNumber = verse.hizb_number || (verse.hizb_quarter ? Math.ceil(verse.hizb_quarter / 4) : null);

  if (type === "first_word_after") {
    const cutIndex = Math.floor(Math.random() * (words.length - 2)) + 1;
    const snippet = words.slice(0, cutIndex).join(" ");
    const correct = words[cutIndex];
    return {
      q: isEnglish ? "What is the first word after this snippet?" : "Apa kata pertama setelah potongan ini?",
      stem: hideText ? null : snippet,
      opts: pickWrongWords(words, correct, 3),
      ans: correct,
      meta: `${surahName} ${verse.verse_key || ""}`.trim(),
    };
  }

  if (type === "last_word") {
    const correct = words[words.length - 1];
    return {
      q: isEnglish ? "What is the last word of this verse?" : "Apa kata terakhir dari ayat ini?",
      stem: hideText ? null : verseText,
      opts: pickWrongWords(words, correct, 3),
      ans: correct,
      meta: `${surahName} ${verse.verse_key || ""}`.trim(),
    };
  }

  if (type === "bridge_next_ayah") {
    if (ayahNumber && ayahNumber < surah.numberOfAyahs) {
      const nextVerse = await withTimeout(quranApi.getVerse(surah.number, Number(ayahNumber) + 1), 7000);
      const nextWords = getVerseWords(nextVerse);
      if (nextWords.length === 0) return null;
      const correct = nextWords[0];
      const opts = pickWrongWords([...words, ...nextWords], correct, 3);
      return {
        q: isEnglish ? "Which word connects this verse to the next one?" : "Kata apa yang menghubungkan ayat ini dengan ayat berikutnya?",
        stem: hideText ? null : verseText,
        opts,
        ans: correct,
        meta: `${surahName} ${verse.verse_key || ""}`.trim(),
      };
    }
    return null;
  }

  if (type === "ayah_number") {
    if (!ayahNumber) return null;
    const opts = buildNumberOptions(Number(ayahNumber), 1, surah.numberOfAyahs);
    return {
      q: isEnglish ? "What is the verse number?" : "Sebutkan nomor ayat.",
      stem: hideText ? null : verseText,
      opts,
      ans: String(ayahNumber),
      meta: `${surahName}`.trim(),
    };
  }

  if (type === "surah_name") {
    const wrong = pickWrongSurahs(Math.max(0, surahIndex), 3).map((x) => x.englishName);
    const opts = [surahName, ...wrong].sort(() => Math.random() - 0.5);
    return {
      q: isEnglish ? "This verse is from which surah?" : "Termasuk surah apa?",
      stem: hideText ? null : verseText,
      opts,
      ans: surahName,
    };
  }

  if (type === "page_number") {
    if (!pageNumber) return null;
    const opts = buildNumberOptions(Number(pageNumber), 1, 604);
    return {
      q: isEnglish ? "Which page is this verse on?" : "Termasuk halaman berapa?",
      stem: hideText ? null : verseText,
      opts,
      ans: String(pageNumber),
      meta: `${surahName} ${verse.verse_key || ""}`.trim(),
    };
  }

  if (type === "juz_number") {
    if (!juzNumber) return null;
    const opts = buildNumberOptions(Number(juzNumber), 1, 30);
    return {
      q: isEnglish ? "Which juz is this verse in?" : "Termasuk juz berapa?",
      stem: hideText ? null : verseText,
      opts,
      ans: String(juzNumber),
      meta: `${surahName} ${verse.verse_key || ""}`.trim(),
    };
  }

  if (type === "hizb_number") {
    if (!hizbNumber) return null;
    const opts = buildNumberOptions(Number(hizbNumber), 1, 60);
    return {
      q: isEnglish ? "Which hizb is this verse in?" : "Termasuk hizb berapa?",
      stem: hideText ? null : verseText,
      opts,
      ans: String(hizbNumber),
      meta: `${surahName} ${verse.verse_key || ""}`.trim(),
    };
  }

  if (type === "word_to_meaning") {
    const pairs = getWordPairs(verse);
    if (pairs.length < 4) return null;
    const pick = pairs[Math.floor(Math.random() * pairs.length)];
    const wrong = pairs.filter((p) => p.meaning !== pick.meaning).map((p) => p.meaning);
    const opts = [pick.meaning, ...wrong.sort(() => Math.random() - 0.5).slice(0, 3)].sort(() => Math.random() - 0.5);
    return {
      q: isEnglish ? "What is the meaning of this word?" : "Apa arti kata ini?",
      stem: hideText ? null : pick.arabic,
      opts,
      ans: pick.meaning,
      meta: `${surahName} ${verse.verse_key || ""}`.trim(),
    };
  }

  if (type === "meaning_to_word") {
    const pairs = getWordPairs(verse);
    if (pairs.length < 4) return null;
    const pick = pairs[Math.floor(Math.random() * pairs.length)];
    const wrong = pairs.filter((p) => p.arabic !== pick.arabic).map((p) => p.arabic);
    const opts = [pick.arabic, ...wrong.sort(() => Math.random() - 0.5).slice(0, 3)].sort(() => Math.random() - 0.5);
    return {
      q: isEnglish ? `Which Arabic word matches this meaning: "${pick.meaning}"?` : `Kata Arab untuk arti "${pick.meaning}"?`,
      opts,
      ans: pick.arabic,
      meta: `${surahName} ${verse.verse_key || ""}`.trim(),
    };
  }

  if (type === "continue_three") {
    const start = Math.floor(Math.random() * (words.length - 5)) + 1;
    const snippet = words.slice(0, start).join(" ");
    const correct = words.slice(start, start + 3).join(" ");
    const opts = buildContinuationOptions(words, correct, 4);
    return {
      q: isEnglish ? "Continue with the next 3 words." : "Lanjutkan 3 kata berikutnya.",
      stem: hideText ? null : snippet,
      opts,
      ans: correct,
      meta: `${surahName} ${verse.verse_key || ""}`.trim(),
    };
  }

  if (type === "continue_start") {
    const start = 0;
    const snippet = words.slice(start, start + 2).join(" ");
    const correct = words.slice(start + 2, start + 5).join(" ");
    const opts = buildContinuationOptions(words, correct, 4);
    return {
      q: isEnglish ? "Choose the continuation from the start of the verse." : "Pilih lanjutan dari awal ayat.",
      stem: hideText ? null : snippet,
      opts,
      ans: correct,
      meta: `${surahName} ${verse.verse_key || ""}`.trim(),
    };
  }

  if (type === "continue_middle") {
    const start = Math.floor(Math.random() * (words.length - 6)) + 2;
    const snippet = words.slice(start, start + 2).join(" ");
    const correct = words.slice(start + 2, start + 5).join(" ");
    const opts = buildContinuationOptions(words, correct, 4);
    return {
      q: isEnglish ? "Choose the continuation from the middle of the verse." : "Pilih lanjutan dari tengah ayat.",
      stem: hideText ? null : snippet,
      opts,
      ans: correct,
      meta: `${surahName} ${verse.verse_key || ""}`.trim(),
    };
  }

  if (type === "continue_end") {
    const start = Math.max(0, words.length - 6);
    const snippet = words.slice(start, start + 2).join(" ");
    const correct = words.slice(start + 2, start + 5).join(" ");
    const opts = buildContinuationOptions(words, correct, 4);
    return {
      q: isEnglish ? "Choose the continuation from the end of the verse." : "Pilih lanjutan dari akhir ayat.",
      stem: hideText ? null : snippet,
      opts,
      ans: correct,
      meta: `${surahName} ${verse.verse_key || ""}`.trim(),
    };
  }

  if (type === "audio_continue") {
    if (!ayahNumber) return null;
    const audio = await withTimeout(quranApi.getAudio(surah.number, ayahNumber), 7000);
    const start = Math.floor(Math.random() * (words.length - 5)) + 1;
    const correct = words.slice(start, start + 3).join(" ");
    const opts = buildContinuationOptions(words, correct, 4);
    return {
      q: isEnglish ? "Continue from the recitation." : "Lanjutkan dari audio ayat ini.",
      opts,
      ans: correct,
      audio,
      meta: `${surahName} ${verse.verse_key || ""}`.trim(),
    };
  }

  if (type === "ayah_snippet_to_translation") {
    if (words.length < 3) return null;

    // Get full verse translation
    const fullTranslation = await getVerseTranslation(verse, isEnglish);
    if (!fullTranslation || fullTranslation.length < 15) return null;

    // Generate a snippet from the Arabic verse (2-3 words)
    const wordCount = Math.floor(Math.random() * 2) + 2; // 2 or 3 words
    const snippet = generateVerseSnippet(words, wordCount);

    // Create translation options
    const opts = await buildTranslationOptions(fullTranslation, 4, isEnglish);

    // Ensure we have enough options
    if (opts.length < 4) return null;

    return {
      q: isEnglish ? "Which translation matches this Arabic snippet?" : "Terjemahan mana yang cocok dengan potongan Arab ini?",
      stem: snippet,
      opts,
      ans: fullTranslation,
      meta: `${surahName} ${verse.verse_key || ""}`.trim(),
    };
  }

  if (type === "translation_to_ayah_snippet") {
    if (words.length < 3) return null;

    // Get full verse translation
    const fullTranslation = await getVerseTranslation(verse, isEnglish);
    if (!fullTranslation || fullTranslation.length < 15) return null;

    // Generate a snippet from the Arabic verse (2-3 words)
    const wordCount = Math.floor(Math.random() * 2) + 2; // 2 or 3 words
    const correctSnippet = generateVerseSnippet(words, wordCount);

    // Create snippet options (wrong snippets from different parts of the verse)
    const opts = buildSnippetOptions(words, correctSnippet, 4);

    // Ensure we have enough options
    if (opts.length < 4) return null;

    return {
      q: isEnglish ? "Which Arabic snippet matches this translation?" : "Potongan Arab mana yang cocok dengan terjemahan ini?",
      stem: fullTranslation,
      opts,
      ans: correctSnippet,
      meta: `${surahName} ${verse.verse_key || ""}`.trim(),
    };
  }

  return null;
}

async function generateAyahQuiz(count, isEnglish, mode, surahNumbers) {
  const result = [];
  const deadline = Date.now() + 12000; // Increase from 9s to 12s
  const seen = new Set();
  let attempts = 0;

  // Prioritize question types that don't need API calls
  // These can use cached verse data
  const priorityTypes = ["surah_name", "ayah_number", "page_number", "juz_number", "hizb_number"];
  const secondaryTypes = ["continue_start", "continue_middle", "continue_end", "continue_three", "first_word_after", "last_word"];

  while (result.length < count && attempts < count * 15) {
    if (Date.now() > deadline) break;
    attempts += 1;

    // Use priority types first
    let typePool = [];
    if (result.length < Math.ceil(count / 2)) {
      // First half: use priority types (less API intensive)
      typePool = [...priorityTypes];
    } else {
      // Second half: use all types
      typePool = [...getAyahQuestionTypes(mode)];
    }

    let question = null;
    while (typePool.length > 0 && !question) {
      if (Date.now() > deadline) break;
      const type = typePool.splice(Math.floor(Math.random() * typePool.length), 1)[0];
      try {
        question = await buildAyahQuestion(type, isEnglish, mode, surahNumbers);
      } catch (error) {
        // Skip this type on error
        continue;
      }
    }

    if (question) {
      const signature = getQuestionSignature(question);
      if (signature && !seen.has(signature)) {
        seen.add(signature);
        result.push({ id: result.length, ...question });
      }
    }
  }

  // If we still don't have enough questions, add some basic surah questions as fallback
  if (result.length < count) {
    const surahPool = surahNumbers?.length
      ? SURAHS.filter((s) => surahNumbers.includes(s.number))
      : SURAHS;

    for (let i = result.length; i < count; i++) {
      const randomSurah = surahPool[Math.floor(Math.random() * surahPool.length)];
      const fallbackQuestion = {
        id: i,
        q: isEnglish
          ? `How many verses are in Surah ${randomSurah.englishName}?`
          : `Berapa jumlah ayat dalam Surah ${randomSurah.englishName}?`,
        opts: buildNumberOptions(randomSurah.numberOfAyahs, 3, 286),
        ans: String(randomSurah.numberOfAyahs),
      };
      const signature = getQuestionSignature(fallbackQuestion);
      if (!seen.has(signature)) {
        seen.add(signature);
        result.push(fallbackQuestion);
      }
    }
  }

  return result;
}

async function generateMemorizationQuiz(count, isEnglish, translations, getMeta, ayahMode, memorizedSurahNumbers) {
  const result = [];
  const surahPool = SURAHS.filter((s) => memorizedSurahNumbers.includes(s.number));
  if (surahPool.length === 0) return result;
  const questionTypes = [...QUESTION_TYPES, ...getAyahQuestionTypes(ayahMode)];
  const seen = new Set();
  let attempts = 0;
  while (result.length < count && attempts < count * 30) {
    attempts += 1;
    const type = questionTypes[Math.floor(Math.random() * questionTypes.length)];
    let question = null;
    if (AYAH_COMPLETION_TYPES.includes(type)) {
      question = await buildAyahQuestion(type, isEnglish, ayahMode, memorizedSurahNumbers);
    } else {
      const surah = surahPool[Math.floor(Math.random() * surahPool.length)];
      const surahIndex = SURAHS.findIndex((item) => item.number === surah.number);
      question = await buildQuestion(type, surah, surahIndex, isEnglish, translations, getMeta);
    }
    if (question) {
      const signature = getQuestionSignature(question);
      if (signature && !seen.has(signature)) {
        seen.add(signature);
        result.push({ id: result.length, ...question });
      }
    }
  }
  return result;
}

async function generateSurahHafalQuiz(count, isEnglish, translations, getMeta, surahNumber, memorizedAyahsList) {
  const result = [];
  const surah = SURAHS.find(s => s.number === surahNumber);
  if (!surah) return result;

  const surahIndex = SURAHS.findIndex(s => s.number === surahNumber);
  const seen = new Set();
  let attempts = 0;
  const maxAttempts = count * 8; // Further reduce attempts

  // Focus on local data questions (no API calls needed)
  const surahQuestionTypes = [
    "number_to_name",
    "arabic_to_name",
    "name_to_number",
    "name_to_meaning",
    "revelation_type",
    "number_of_ayahs",
    "total_surahs", // Added - no API needed
  ];

  // Only use simple ayah completion types (no audio, no bridge)
  const simpleAyahTypes = ["ayah_number", "surah_name"];

  try {
    // First, generate all local data questions (fast, no API)
    for (const type of surahQuestionTypes) {
      if (result.length >= count) break;
      try {
        const question = await buildQuestion(type, surah, surahIndex, isEnglish, translations, getMeta, []);
        if (question) {
          const signature = getQuestionSignature(question);
          if (signature && !seen.has(signature)) {
            seen.add(signature);
            result.push({ id: result.length, ...question });
          }
        }
      } catch (error) {
        // Skip this type
        continue;
      }
    }

    // Then add a few ayah questions (slow, requires API) - max 3-4
    const maxAyahQuestions = Math.min(4, count - result.length);
    let ayahAttempts = 0;
    while (result.length < count && ayahAttempts < maxAyahQuestions * 3) {
      ayahAttempts++;

      try {
        const type = simpleAyahTypes[Math.floor(Math.random() * simpleAyahTypes.length)];
        const question = await withTimeout(buildAyahQuestion(type, isEnglish, "easy", [surahNumber]), 3000);

        if (question) {
          const signature = getQuestionSignature(question);
          if (signature && !seen.has(signature)) {
            seen.add(signature);
            result.push({ id: result.length, ...question });
          }
        }
      } catch (error) {
        // Skip and continue
        continue;
      }
    }

    // If still need more, shuffle and add duplicate local questions
    while (result.length < count && result.length > 0) {
      const randomIndex = Math.floor(Math.random() * result.length);
      const baseQuestion = result[randomIndex];

      // Create variation with different ID
      const variation = { ...baseQuestion, id: result.length };
      result.push(variation);

      // Prevent infinite loop
      if (result.length >= count * 2) break;
    }

    // Trim to exact count
    return result.slice(0, count);
  } catch (error) {
    console.error("Error in generateSurahHafalQuiz:", error);
    return result.slice(0, count);
  }
}

function QuizPlay() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const isEnglish = language === "en";
  const category = searchParams.get("category") || "random";
  const surahNumber = searchParams.get("surah") ? parseInt(searchParams.get("surah")) : null;
  const [qs, setQs] = useState([]);
  const [idx, setIdx] = useState(0);
  const [sel, setSel] = useState(null);
  const [done, setDone] = useState(false);
  const [score, setScore] = useState(0);
  const [time, setTime] = useState(30);
  const [fin, setFin] = useState(false);
  const [load, setLoad] = useState(true);
  const [nextLoading, setNextLoading] = useState(false);
  const [surahTranslations, setSurahTranslations] = useState({});
  const [ayahMode, setAyahMode] = useState("easy");
  const [currentSurah, setCurrentSurah] = useState(null);
  const [memorizedAyahs, setMemorizedAyahs] = useState([]);
  const metaCacheRef = useRef({});
  const memorizedSurahNumbersRef = useRef([]);
  const autoAdvanceRef = useRef(false);
  const autoAdvanceTimeoutRef = useRef(null);

  const getMeta = useCallback(async (surahNumber) => {
    if (metaCacheRef.current[surahNumber]) return metaCacheRef.current[surahNumber];
    try {
      const meta = await getSurahFirstAyahMeta(surahNumber);
      metaCacheRef.current[surahNumber] = meta;
      return meta;
    } catch (error) {
      metaCacheRef.current[surahNumber] = null;
      return null;
    }
  }, []);

  const getQuestionTypes = useCallback(() => {
    if (category === "surah-names") return NAME_ONLY_TYPES;
    if (category === "translation") return TRANSLATION_QUIZ_TYPES;
    return QUESTION_TYPES;
  }, [category]);

  useEffect(() => {
    let isMounted = true;
    const loadQuiz = async () => {
      setLoad(true);
      try {
        let translations = {};
        if (category !== "ayah-completion") {
          try {
            translations = await withTimeout(getSurahTranslations(isEnglish ? "en" : "id"), 7000);
          } catch (error) {
            translations = {};
          }
        }
        if (!isMounted) return;
        setSurahTranslations(translations);
        let questions = [];

        if (category === "surah-hafal" && surahNumber) {
          // Load surah-specific hafal quiz
          try {
            const surah = SURAHS.find(s => s.number === surahNumber);
            if (!surah) {
              console.error("Surah not found:", surahNumber);
              questions = [];
            } else {
              setCurrentSurah(surah);

              // Get memorized ayahs for this surah
              try {
                const memRes = await withTimeout(apiClient.get("/memorization"), 7000);
                const surahMem = (memRes.data || []).find(m => m.surah_number === surahNumber);
                const memAyahs = surahMem?.memorized_ayahs || [];
                setMemorizedAyahs(memAyahs);
              } catch (error) {
                console.error("Error fetching memorization:", error);
                setMemorizedAyahs([]);
              }

              console.log("Generating surah hafal quiz for:", surahNumber);
              questions = await withTimeout(
                generateSurahHafalQuiz(10, isEnglish, translations, getMeta, surahNumber, memAyahs),
                15000
              );
              console.log("Generated questions:", questions.length);
            }
          } catch (error) {
            console.error("Error loading surah hafal quiz:", error);
            questions = [];
          }
        } else if (category === "memorization") {
          try {
            const memRes = await withTimeout(apiClient.get("/memorization"), 7000);
            const completed = (memRes.data || []).filter((m) => (m.memorized_ayahs?.length || 0) >= m.total_ayahs);
            memorizedSurahNumbersRef.current = completed.map((m) => m.surah_number);
          } catch (error) {
            memorizedSurahNumbersRef.current = [];
          }
          questions = await withTimeout(
            generateMemorizationQuiz(10, isEnglish, translations, getMeta, ayahMode, memorizedSurahNumbersRef.current),
            10000
          );
        } else if (category === "ayah-completion") {
          // Load more initial questions for ayah-completion (5 instead of 1)
          try {
            questions = await withTimeout(generateAyahQuiz(5, isEnglish, ayahMode), 15000);
            // If we got less than 3 questions, add fallback
            if (questions.length < 3) {
              const fallback = generateFallbackQuestions(5 - questions.length, isEnglish);
              questions = [...questions, ...fallback];
            }
          } catch (error) {
            console.error("Error loading ayah-completion quiz, using fallback:", error);
            questions = generateFallbackQuestions(5, isEnglish);
          }
          // Ensure we always have at least some questions
          if (questions.length === 0) {
            questions = generateFallbackQuestions(5, isEnglish);
          }
        } else {
          questions = await withTimeout(generateQuiz(10, isEnglish, translations, getMeta, getQuestionTypes()), 10000);
        }
        if (!isMounted) return;
        setQs(questions);
      } catch (error) {
        console.error("Error in loadQuiz:", error);
        if (!isMounted) return;
        setQs([]);
      } finally {
        if (!isMounted) return;
        setLoad(false);
      }
    };
    loadQuiz();
    return () => {
      isMounted = false;
    };
  }, [isEnglish, category, ayahMode, surahNumber]);

  useEffect(() => {
    if (fin || done || load || qs.length === 0) return;
    const t = setInterval(() => {
      setTime(p => { if (p <= 1) { setSel(null); setDone(true); return 30; } return p - 1; });
    }, 1000);
    return () => clearInterval(t);
  }, [idx, done, fin, load, qs.length]);

  const pick = useCallback((a) => {
    if (done) return;
    setSel(a);
    setDone(true);
    if (a === qs[idx].ans) setScore(s => s + 1);
  }, [done, qs, idx]);

  const next = useCallback(async () => {
    autoAdvanceRef.current = true;
    if (nextLoading) return;
    if (autoAdvanceTimeoutRef.current) {
      clearTimeout(autoAdvanceTimeoutRef.current);
      autoAdvanceTimeoutRef.current = null;
    }
    try {
      const prev = qs[idx];
      const verseKey = parseVerseKeyFromMeta(prev?.meta);
      if (verseKey) updateSrs(verseKey, sel === prev?.ans);
    } catch {}
    if (idx < qs.length - 1) {
      setIdx(idx + 1);
      setSel(null);
      setDone(false);
      setTime(30);
    } else {
      if (category === "ayah-completion" && qs.length < TOTAL_QUESTIONS) {
        setNextLoading(true);
        try {
          // Load 2 more questions at a time instead of 1
          const more = await withTimeout(generateAyahQuiz(2, isEnglish, ayahMode), 10000);
          if (more.length === 0) {
            // If we can't generate more questions but have some, finish
            if (qs.length >= 3) {
              setFin(true);
            } else {
              // Try to add fallback questions and continue
              const fallbackQuestions = generateFallbackQuestions(TOTAL_QUESTIONS - qs.length, isEnglish);
              if (fallbackQuestions.length > 0) {
                setQs(prev => {
                  const offset = prev.length;
                  const mapped = fallbackQuestions.map((q, i) => ({ ...q, id: offset + i }));
                  return [...prev, ...mapped];
                });
                setIdx(idx + 1);
                setSel(null);
                setDone(false);
                setTime(30);
              } else {
                setFin(true);
              }
            }
          } else {
            setQs(prev => {
              const offset = prev.length;
              const mapped = more.map((q, i) => ({ ...q, id: offset + i }));
              return [...prev, ...mapped];
            });
            setIdx(idx + 1);
            setSel(null);
            setDone(false);
            setTime(30);
          }
        } catch (error) {
          console.error("Error loading more questions:", error);
          // On error, try to finish if we have enough questions
          if (qs.length >= 3) {
            setFin(true);
          } else {
            // Add fallback questions
            const fallbackQuestions = generateFallbackQuestions(TOTAL_QUESTIONS - qs.length, isEnglish);
            if (fallbackQuestions.length > 0) {
              setQs(prev => {
                const offset = prev.length;
                const mapped = fallbackQuestions.map((q, i) => ({ ...q, id: offset + i }));
                return [...prev, ...mapped];
              });
              setIdx(idx + 1);
              setSel(null);
              setDone(false);
              setTime(30);
            } else {
              setFin(true);
            }
          }
        } finally {
          setNextLoading(false);
        }
      } else {
        setFin(true);
        try {
          await apiClient.post("/quiz/submit", { correct_answers: score, total_questions: qs.length, points_earned: score * 10 });
        } catch (e) {}
      }
    }
  }, [idx, qs.length, score, category, isEnglish, ayahMode, nextLoading]);

  useEffect(() => {
    autoAdvanceRef.current = false;
  }, [idx, fin]);

  useEffect(() => {
    if (fin || load || !done || qs.length === 0 || nextLoading) return;
    if (autoAdvanceRef.current) return;
    autoAdvanceRef.current = true;
    autoAdvanceTimeoutRef.current = setTimeout(() => {
      next();
    }, 900);
    return () => {
      if (autoAdvanceTimeoutRef.current) {
        clearTimeout(autoAdvanceTimeoutRef.current);
        autoAdvanceTimeoutRef.current = null;
      }
    };
  }, [done, fin, load, qs.length, next, nextLoading]);

  const restart = async () => {
    setLoad(true);
    try {
      let questions = [];
      if (category === "surah-hafal" && surahNumber) {
        questions = await withTimeout(
          generateSurahHafalQuiz(10, isEnglish, surahTranslations, getMeta, surahNumber, memorizedAyahs),
          10000
        );
      } else if (category === "memorization") {
        questions = await withTimeout(
          generateMemorizationQuiz(10, isEnglish, surahTranslations, getMeta, ayahMode, memorizedSurahNumbersRef.current),
          10000
        );
      } else if (category === "ayah-completion") {
        try {
          questions = await withTimeout(generateAyahQuiz(5, isEnglish, ayahMode), 15000);
          // If we got less than 3 questions, add fallback
          if (questions.length < 3) {
            const fallback = generateFallbackQuestions(5 - questions.length, isEnglish);
            questions = [...questions, ...fallback];
          }
          // Ensure we always have at least some questions
          if (questions.length === 0) {
            questions = generateFallbackQuestions(5, isEnglish);
          }
        } catch (error) {
          console.error("Error loading ayah-completion quiz, using fallback:", error);
          questions = generateFallbackQuestions(5, isEnglish);
        }
      } else {
        questions = await withTimeout(generateQuiz(10, isEnglish, surahTranslations, getMeta, getQuestionTypes()), 10000);
      }
      setQs(questions);
      setIdx(0);
      setSel(null);
      setDone(false);
      setScore(0);
      setTime(30);
      setFin(false);
    } finally {
      setLoad(false);
    }
  };

  if (load) return (
    <IslamicLoadingScreen
      title={isEnglish ? "Loading Quiz" : "Memuat Quiz"}
      language={isEnglish ? "en" : "id"}
      size="large"
    />
  );

  if (qs.length === 0) {
    const hasMemorized = memorizedSurahNumbersRef.current.length > 0;
    let emptyMessage = "";
    if (category === "surah-hafal") {
      const surahName = currentSurah?.englishName || `Surah ${surahNumber}`;
      emptyMessage = isEnglish
        ? `Unable to load questions for ${surahName}. Please try again.`
        : `Gagal memuat soal untuk ${surahName}. Silakan coba lagi.`;
    } else if (category === "memorization") {
      emptyMessage = hasMemorized
        ? (isEnglish ? "Unable to load questions. Please try again." : "Gagal memuat soal. Silakan coba lagi.")
        : (isEnglish ? "No completed memorization yet. Complete a surah to start this quiz." : "Belum ada hafalan selesai. Selesaikan satu surah untuk mulai quiz ini.");
    } else {
      emptyMessage = isEnglish ? "Unable to load questions. Please try again." : "Gagal memuat soal. Silakan coba lagi.";
    }
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <PageHeader title={isEnglish ? "Quiz" : "Quiz"} showBack />
        <div className="page-container">
          <Card className="text-center border-2 shadow-xl quiz-scale-in">
            <CardContent className="p-6 md:p-10 space-y-4 md:space-y-6">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <RotateCcw className="w-8 h-8 md:w-10 md:h-10 text-primary" />
              </div>
              <div>
                <h3 className="text-lg md:text-xl font-bold mb-2">{isEnglish ? "No Questions Available" : "Tidak Ada Soal"}</h3>
                <p className="text-sm md:text-base text-muted-foreground">{emptyMessage}</p>
              </div>
              <Button className="w-full shadow-lg hover:shadow-xl transition-all text-sm md:text-base" size="lg" onClick={restart}>
                <RotateCcw className="w-4 h-4 md:w-5 md:h-5 mr-2" /> {isEnglish ? "Retry" : "Coba Lagi"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (fin) {
    const totalCount = category === "ayah-completion" ? TOTAL_QUESTIONS : qs.length;
    const pct = Math.round((score / totalCount) * 100);
    const isPerfect = pct === 100;
    const isGood = pct >= 70;
    const emoji = isPerfect ? "🎉" : isGood ? "👏" : "💪";

    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5" data-testid="quiz-result">
        <PageHeader title={isEnglish ? "Quiz Result" : "Hasil Quiz"} showBack />
        <div className="page-container">
          <Card className="text-center border-2 shadow-2xl quiz-scale-in overflow-hidden">
            <div className={`bg-gradient-to-r ${isPerfect ? 'from-yellow-400 via-amber-400 to-yellow-500' : isGood ? 'from-primary via-primary/80 to-primary/60' : 'from-muted-foreground/20 to-muted-foreground/10'} text-white p-6 md:p-8`}>
              <div className="text-4xl md:text-6xl mb-3 md:mb-4">{emoji}</div>
              <div className={`w-24 h-24 md:w-32 md:h-32 rounded-full ${isPerfect ? 'bg-white/20' : 'bg-white/10'} backdrop-blur-sm flex items-center justify-center mx-auto mb-3 md:mb-4 quiz-glow`}>
                <Trophy className="w-12 h-12 md:w-16 md:h-16 text-white" />
              </div>
            </div>
            <CardContent className="p-6 md:p-8 space-y-4 md:space-y-6">
              <div className="space-y-1 md:space-y-2">
                <h2 className={`text-4xl md:text-5xl font-bold ${isPerfect ? 'text-amber-600' : isGood ? 'text-primary' : 'text-foreground'}`}>{pct}%</h2>
                <p className="text-sm md:text-lg text-muted-foreground">{isEnglish ? `${score} of ${totalCount} correct answers` : `${score} dari ${totalCount} jawaban benar`}</p>
              </div>

              <div className={`rounded-xl md:rounded-2xl p-4 md:p-6 ${isPerfect ? 'bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-200' : isGood ? 'bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/20' : 'bg-gradient-to-br from-muted to-muted/50 border-2 border-border'}`}>
                <p className="text-xs md:text-sm text-muted-foreground mb-1 md:mb-2">{isEnglish ? "Points earned" : "Poin didapat"}</p>
                <p className={`text-3xl md:text-4xl font-bold ${isPerfect ? 'text-amber-600' : isGood ? 'text-primary' : 'text-foreground'}`}>+{score * 10}</p>
              </div>

              <div className="grid grid-cols-2 gap-3 md:gap-4 pt-3 md:pt-4">
                <div className="text-center p-3 md:p-4 rounded-xl bg-muted/50">
                  <p className="text-2xl md:text-3xl font-bold text-green-600">{score}</p>
                  <p className="text-[10px] md:text-xs text-muted-foreground">{isEnglish ? "Correct" : "Benar"}</p>
                </div>
                <div className="text-center p-3 md:p-4 rounded-xl bg-muted/50">
                  <p className="text-2xl md:text-3xl font-bold text-red-600">{totalCount - score}</p>
                  <p className="text-[10px] md:text-xs text-muted-foreground">{isEnglish ? "Wrong" : "Salah"}</p>
                </div>
              </div>

              <div className="space-y-2 md:space-y-3 pt-3 md:pt-4">
                <Button className="w-full shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] text-sm md:text-base" size="lg" onClick={restart}>
                  <RotateCcw className="w-4 h-4 md:w-5 md:h-5 mr-2" /> {isEnglish ? "Play Again" : "Main Lagi"}
                </Button>
                <Button variant="outline" className="w-full hover:bg-muted transition-all text-sm md:text-base" size="lg" onClick={() => nav("/quiz")}>
                  <Home className="w-4 h-4 md:w-5 md:h-5 mr-2" /> {isEnglish ? "Back to Quiz Menu" : "Kembali ke Menu Quiz"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const cur = qs[idx];
  const totalCount = category === "ayah-completion" ? TOTAL_QUESTIONS : qs.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5" data-testid="quiz-play-page">
      <PageHeader
        title={isEnglish ? `Question ${idx + 1}/${totalCount}` : "Soal " + (idx + 1) + "/" + totalCount}
        showBack
        rightContent={
          <div className={`flex items-center gap-1.5 md:gap-2 px-2 md:px-4 py-1.5 md:py-2 rounded-full ${time <= 10 ? 'bg-red-100 text-red-600 animate-pulse' : 'bg-primary/10 text-primary'}`}>
            <Clock className="w-4 h-4 md:w-5 md:h-5" />
            <span className="font-bold text-sm md:text-lg">{time}s</span>
          </div>
        }
      />

      <div className="page-container space-y-4 md:space-y-6">
        {/* Progress Bar */}
        <div className="space-y-1.5 md:space-y-2">
          <Progress value={((idx + 1) / totalCount) * 100} className="h-2 md:h-3 shadow-inner" />
          <div className="flex justify-between text-[10px] md:text-xs text-muted-foreground">
            <span>{isEnglish ? "Progress" : "Kemajuan"}</span>
            <span className="font-medium">{Math.round(((idx + 1) / totalCount) * 100)}%</span>
          </div>
        </div>

        {/* Surah Info Card - Only for surah-hafal */}
        {category === "surah-hafal" && currentSurah && (
          <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-2 border-primary/20">
            <CardContent className="p-3 md:p-4">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-white font-bold text-sm md:text-lg flex-shrink-0">
                  {currentSurah.number}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs md:text-sm text-muted-foreground">{isEnglish ? "Quiz for" : "Quiz untuk"}</p>
                  <p className="font-bold text-sm md:text-lg truncate">{currentSurah.englishName}</p>
                  <p className="text-[10px] md:text-xs text-muted-foreground">{currentSurah.englishNameTranslation}</p>
                </div>
                {memorizedAyahs.length > 0 && (
                  <div className="text-right flex-shrink-0">
                    <p className="text-lg md:text-xl font-bold text-primary">{memorizedAyahs.length}</p>
                    <p className="text-[10px] md:text-xs text-muted-foreground">{isEnglish ? "memorized" : "ayat"}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Score Card */}
        <div className="flex justify-center">
          <div className="inline-flex items-center gap-2 md:gap-3 px-3 md:px-6 py-2 md:py-3 rounded-full bg-gradient-to-r from-primary/10 to-primary/5 border-2 border-primary/20 shadow-md">
            <Trophy className="w-4 h-4 md:w-5 md:h-5 text-primary" />
            <span className="text-xs md:text-sm font-medium text-muted-foreground">{isEnglish ? "Score" : "Skor"}:</span>
            <span className="text-lg md:text-2xl font-bold text-primary">{score}</span>
          </div>
        </div>

        {/* Difficulty Mode Selector */}
        {(category === "ayah-completion" || category === "memorization") && (
          <div className="quiz-fade-in">
            <p className="text-xs md:text-sm text-muted-foreground text-center mb-2 md:mb-3">{isEnglish ? "Select Difficulty" : "Pilih Kesulitan"}</p>
            <div className="flex flex-wrap gap-1.5 md:gap-2 justify-center">
              <Button
                size="sm"
                variant={ayahMode === "easy" ? "default" : "outline"}
                onClick={() => setAyahMode("easy")}
                className={ayahMode === "easy" ? "shadow-md text-xs md:text-sm" : "text-xs md:text-sm"}
              >
                {isEnglish ? "Easy" : "Easy"}
              </Button>
              <Button
                size="sm"
                variant={ayahMode === "medium" ? "default" : "outline"}
                onClick={() => setAyahMode("medium")}
                className={ayahMode === "medium" ? "shadow-md text-xs md:text-sm" : "text-xs md:text-sm"}
              >
                {isEnglish ? "Medium" : "Medium"}
              </Button>
              <Button
                size="sm"
                variant={ayahMode === "hard" ? "default" : "outline"}
                onClick={() => setAyahMode("hard")}
                className={ayahMode === "hard" ? "shadow-md text-xs md:text-sm" : "text-xs md:text-sm"}
              >
                {isEnglish ? "Hard" : "Hard"}
              </Button>
              <Button
                size="sm"
                variant={ayahMode === "hafiz" ? "default" : "outline"}
                onClick={() => setAyahMode("hafiz")}
                className={ayahMode === "hafiz" ? "shadow-md text-xs md:text-sm" : "text-xs md:text-sm"}
              >
                {isEnglish ? "Hafiz" : "Hafiz"}
              </Button>
            </div>
          </div>
        )}

        {/* Question Card */}
        <Card className="border-2 shadow-xl quiz-fade-in overflow-hidden">
          <CardContent className="p-4 md:p-6 space-y-3 md:space-y-5">
            <div className="space-y-2 md:space-y-3">
              <div className="flex items-start gap-2 md:gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5 md:mt-1">
                  <span className="text-sm md:text-base text-primary font-bold">{idx + 1}</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm md:text-lg font-medium leading-relaxed">{cur?.q}</p>
                </div>
              </div>
              {cur?.meta && (
                <div className="flex items-center gap-1.5 md:gap-2 text-xs md:text-sm">
                  <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-primary"></div>
                  <p className="text-muted-foreground">{cur.meta}</p>
                </div>
              )}
            </div>

            {cur?.stem && (
              <div className="rounded-xl md:rounded-2xl bg-gradient-to-br from-primary/5 to-primary/10 p-5 md:p-7 text-center border-2 border-primary/20 shadow-inner">
                <ArabicText
                  text={cur.stem}
                  size="large"
                  className="text-xl md:text-2xl lg:text-3xl text-foreground leading-loose"
                />
              </div>
            )}

            {cur?.audio && (
              <div className="flex justify-center p-3 md:p-4 bg-muted/30 rounded-xl">
                <audio controls src={cur.audio} className="w-full max-w-md" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Options */}
        <div className="space-y-2 md:space-y-3">
          {cur?.opts.map((o, i) => {
            const isSel = sel === o;
            const isOk = o === cur.ans;
            let c = "quiz-option";
            if (done && isOk) c += " quiz-option-correct";
            else if (done && isSel && !isOk) c += " quiz-option-wrong";
            else if (isSel) c += " quiz-option-selected";

            return (
              <button
                key={i}
                className={`${c} ${!done ? 'quiz-fade-in' : ''}`}
                style={{ animationDelay: `${i * 0.1}s` }}
                onClick={() => pick(o)}
                disabled={done}
                data-testid={"quiz-option-" + i}
              >
                <div className="flex items-center gap-2 md:gap-4">
                  <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full border-2 flex items-center justify-center text-sm md:text-base font-bold flex-shrink-0 ${
                    done && isOk ? "border-green-500 bg-green-500 text-white shadow-lg" :
                    done && isSel && !isOk ? "border-red-500 bg-red-500 text-white shadow-lg" :
                    "border-primary/30 bg-primary/5 text-primary"
                  }`}>
                    {String.fromCharCode(65 + i)}
                  </div>
                  {containsArabic(o) ? (
                    <span className="flex-1 font-uthmani hafssmart text-lg md:text-xl lg:text-2xl text-right leading-relaxed" dir="rtl" lang="ar">{o}</span>
                  ) : (
                    <span className="flex-1 text-left text-sm md:text-base font-medium">{o}</span>
                  )}
                  {done && isOk && (
                    <div className="flex-shrink-0">
                      <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-green-500" />
                    </div>
                  )}
                  {done && isSel && !isOk && (
                    <div className="flex-shrink-0">
                      <XCircle className="w-5 h-5 md:w-6 md:h-6 text-red-500" />
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Next Button */}
        {done && (
          <Button
            className="w-full shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] quiz-fade-in text-sm md:text-base"
            size="lg"
            onClick={next}
            disabled={nextLoading}
          >
            {idx < qs.length - 1 ? (
              <>{isEnglish ? "Next Question" : "Soal Berikutnya"} <ChevronRight className="w-4 h-4 md:w-5 md:h-5 ml-2" /></>
            ) : (
              <>{isEnglish ? "View Result" : "Lihat Hasil"} <Trophy className="w-4 h-4 md:w-5 md:h-5 ml-2" /></>
            )}
          </Button>
        )}

        {/* Loading State */}
        {nextLoading && (
          <div className="text-center py-4 md:py-6 quiz-fade-in">
            <div className="inline-flex items-center gap-2 md:gap-3 px-4 md:px-6 py-2 md:py-3 rounded-full bg-muted">
              <div className="w-4 h-4 md:w-5 md:h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <span className="text-xs md:text-sm text-muted-foreground">{isEnglish ? "Loading next question..." : "Memuat soal berikutnya..."}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default QuizPlay;
